import React from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import ResourceLink from "./ResourceLink";
import {
  Resource,
  getResourceFormat,
  getResourceResolution,
} from "shared/Resource";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ReactComponent as AVIFIcon } from "assets/icons/site/resources/formats/Avif.svg";
import { ReactComponent as JPGIcon } from "assets/icons/site/resources/formats/Jpeg.svg";
import { useTheme } from "@mui/material";

export interface ResourceLinkProps {
  resources?: Array<Resource> | undefined;
}

const ResourcesFrame = ({ resources, ...props }: ResourceLinkProps) => {
  if (!resources || !resources.length) {
    return null;
  }

  // TODO: Move this logic out of the resource frame and into the the intial post result because we cannot reorder
  // readonly arrays in the rtk query
  let orderedResources = [...resources];
  orderedResources = orderResourceByType(orderedResources).map((resource) => ({
    ...resource,
    resolution: getResourceResolution(resource),
    format: getResourceFormat(resource),
  }));

  const resourceMap = mapByResourceType(orderedResources);

  // Remove viefwers from the resourceMap
  const resourceViewers = resourceMap.splice(2, 1)[0];

  return (
    <Stack>
      <Typography textAlign="left" fontSize="1.5rem" fontWeight="bold" p={2}>
        Links
      </Typography>
      <DownloadResources resources={[...resourceMap[0], ...resourceMap[1]]} />
      <Stack>
        <Grid container>
          <Grid xs={1}>
            <ResourceTitle title={"Read"} fontSize="1.25rem" />
          </Grid>
          <Grid>
            <Grid xs={11} paddingLeft={1.8}>
              <Stack direction="row">
                {resourceViewers.map((resource) => (
                  <ResourceLink key={resource.uri} {...resource} />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
};

function getResourceCategory(value: number): string {
  switch (value) {
    case 0:
      return "P2P";
    case 1:
      return "DDL";
    case 2:
      return "Viewers";
    default:
      return "Unknown";
  }
}

function orderResourceByType(resources: Array<Resource>): Array<Resource> {
  return (resources = resources.sort((r1, r2) => {
    if (r1.kind > r2.kind) {
      return 1;
    } else if (r1.kind === r2.kind) {
      return 0;
    } else {
      return -1;
    }
  }));
}

function mapByResourceType(resources: Array<Resource>): Array<Array<Resource>> {
  const resourceMap: Array<Array<Resource>> = [[], [], []];

  resources.forEach((resource) => {
    let kind = 1;
    if (resource.kind < 3) {
      // resourece is p2p
      kind = 0;
    } else if (resource.kind === 5) {
      // resource is a viewer
      kind = 2;
    }

    resourceMap[kind].push(resource);
  });

  //  NOTE: the logic for this function is garbage because it's too complicated. We need to add fields for
  // specific resources so that we can pull them down and sort them with ease
  function orderResourceByFormat(resources: Array<Resource>) {
    resources.map((resource, index) => {
      if (
        resource.description.includes("cbz-jpg") ||
        resource.description.includes("cbz-jpeg")
      ) {
        return 0;
      } else if (resource.description.includes("cbz-avif")) {
        return 1;
      }

      return 2;
    });
  }

  return resourceMap;
}

function groupResources(resources: Array<Resource>) {
  let groups: Record<string, Record<string, Resource[]>> = {};

  for (let resource of resources) {
    let format = resource.format;
    let resolution = resource.resolution;

    if (!resolution) {
      resolution = "Unknown";
    }

    if (!format) {
      format = "Unknown";
    }

    if (!groups[resolution]) {
      groups[resolution] = {};
    }

    if (!groups[resolution][format]) {
      groups[resolution][format] = [resource];
    } else {
      groups[resolution][format].push(resource);
    }
  }

  return groups;
}

export interface ResourceTitleProps {
  title: JSX.Element | string;
  fontSize?: string;
  paddingRight?: string | number;
  paddingLeft?: string | number;
  p?: string | number;
}

export const ResourceTitle = ({ title, ...props }: ResourceTitleProps) => {
  return (
    <Stack
      fontWeight={"bold"}
      height={"100%"}
      alignContent="right"
      justifyContent="center"
      {...props}
    >
      {title}
    </Stack>
  );
};

export interface DownloadResourceProps {
  resources: Array<Resource>;
}

function getResourceFormatIcon(format: string): JSX.Element | string {
  switch (format) {
    case "jpg":
    case "jpeg":
      return <JPGIcon viewBox="0 0 88.65 91.7" width={48} height={48} />;
    case "avif":
    case "av1f":
      return <AVIFIcon viewBox="0 0 760 568" height={64} width={64} />;
    default:
      return "Unknown";
  }
}

const DownloadResources = ({ resources, ...props }: DownloadResourceProps) => {
  const theme = useTheme();
  const orderedResources = groupResources(resources);

  return (
    <React.Fragment>
      <Stack>
        <Typography textAlign="start" fontWeight="bold" p={2}>
          Download
        </Typography>

        {Object.entries(orderedResources)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([resolution, formats]) => (
            <Accordion key={resolution} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
                <Typography
                  textAlign="start"
                  fontSize="1.25rem"
                  fontWeight="bold"
                  // p={0.5}
                >
                  {resolution}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(formats).map(([format, res], index) => (
                  <Grid key={format} container>
                    <Grid xs={2} sm={1}>
                      <ResourceTitle title={getResourceFormatIcon(format)} />
                    </Grid>
                    <Grid xs={10} sm={11}>
                      <Stack direction="row" flexWrap={"wrap"}>
                        <Grid container paddingRight={5}>
                          <Grid xs={3} sm={2}>
                            <ResourceTitle title="P2P" />
                          </Grid>
                          <Grid xs={7} sm={8} >
                            <Stack direction="row">
                              {res
                                .sort((a, b) =>
                                  a.description.localeCompare(b.description)
                                )
                                // .sort((a, b) => a.kind - b.kind)
                                .filter((res) => res.kind < 3)
                                .map((resource) => (
                                  <ResourceLink
                                    key={resource.uri}
                                    kind={resource.kind}
                                    uri={resource.uri}
                                    description={resource.description}
                                    missing={resource.missing}
                                  />
                                ))}
                            </Stack>
                          </Grid>
                        </Grid>
                        <Grid container paddingRight={5}>
                          <Grid xs={2}>
                            <ResourceTitle title="DDL" />
                          </Grid>
                          <Grid xs={10}>
                            <Stack direction="row">
                              {res
                                .sort((a, b) =>
                                  a.description.localeCompare(b.description)
                                )
                                // .sort((a, b) => a.kind - b.kind)
                                .filter((res) => res.kind >= 3)
                                .map((resource) => (
                                  <ResourceLink
                                    key={resource.uri}
                                    kind={resource.kind}
                                    uri={resource.uri}
                                    description={resource.description}
                                    missing={resource.missing}
                                  />
                                ))}
                            </Stack>
                          </Grid>
                        </Grid>
                      </Stack>
                    </Grid>
                  </Grid>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
      </Stack>
    </React.Fragment>
  );
};

export default ResourcesFrame;
